/**
 * BreadcrumbNav Component
 * 
 * Context-aware navigation showing the current path hierarchy.
 * Helps users understand where they are in the application.
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { BreadcrumbItem } from '../../types';

export interface BreadcrumbNavProps {
  /** Custom breadcrumb items (optional - will auto-generate from route if not provided) */
  items?: BreadcrumbItem[];
  /** Optional additional CSS classes */
  className?: string;
  /** Show home icon as first item */
  showHome?: boolean;
  /** Home path */
  homePath?: string;
}

/**
 * Route mapping for breadcrumb labels
 */
const routeLabels: Record<string, string> = {
  payments: 'Payments',
  'new': 'New Payment',
  held: 'Fraud Review',
  vendors: 'Vendors',
  ledger: 'Ledger',
  settings: 'Settings',
  reports: 'Reports',
};

/**
 * Generate breadcrumb items from current location
 */
function generateBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const segments = pathname.split('/').filter(Boolean);
  const breadcrumbs: BreadcrumbItem[] = [];
  let currentPath = '';

  for (const segment of segments) {
    currentPath += `/${segment}`;
    
    // Check if segment looks like an ID (UUID or numeric)
    const isId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment) || 
                 /^\d+$/.test(segment);
    
    if (isId) {
      // For IDs, show a shortened version
      breadcrumbs.push({
        label: `ID: ${segment.slice(0, 8)}...`,
        path: currentPath,
      });
    } else {
      breadcrumbs.push({
        label: routeLabels[segment] || segment.charAt(0).toUpperCase() + segment.slice(1),
        path: currentPath,
      });
    }
  }

  return breadcrumbs;
}

/**
 * BreadcrumbNav - Context-aware navigation component
 * 
 * @example
 * // Auto-generated from route
 * <BreadcrumbNav />
 * 
 * @example
 * // Custom items
 * <BreadcrumbNav 
 *   items={[
 *     { label: 'Payments', path: '/payments' },
 *     { label: 'Fraud Review', path: '/payments/held' },
 *     { label: 'Payment #12345' }
 *   ]}
 * />
 */
export const BreadcrumbNav: React.FC<BreadcrumbNavProps> = ({
  items,
  className,
  showHome = true,
  homePath = '/',
}) => {
  const location = useLocation();
  const breadcrumbItems = items || generateBreadcrumbs(location.pathname);

  // Don't show breadcrumbs on home page
  if (location.pathname === '/' && !items) {
    return null;
  }

  return (
    <nav 
      aria-label="Breadcrumb"
      className={cn('flex items-center text-sm text-gray-500', className)}
    >
      <ol className="flex items-center space-x-2">
        {showHome && (
          <li className="flex items-center">
            <Link
              to={homePath}
              className="flex items-center hover:text-gray-700 transition-colors"
              aria-label="Home"
            >
              <Home className="w-4 h-4" />
            </Link>
            {breadcrumbItems.length > 0 && (
              <ChevronRight className="w-4 h-4 mx-2 text-gray-400" />
            )}
          </li>
        )}

        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;

          return (
            <li key={`${item.label}-${index}`} className="flex items-center">
              {isLast ? (
                <span 
                  className="font-medium text-gray-900"
                  aria-current="page"
                >
                  {item.label}
                </span>
              ) : (
                <>
                  <Link
                    to={item.path || '#'}
                    className="hover:text-gray-700 transition-colors"
                  >
                    {item.label}
                  </Link>
                  <ChevronRight className="w-4 h-4 mx-2 text-gray-400" />
                </>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default BreadcrumbNav;
